import type {
	CreateProjectReviewInput,
	ProjectReviewEligibility,
	ProjectReviewEligibilityTarget,
} from "@shared/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { StarRatingInput } from "@/components/ui/review-stars";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { saveProjectReview } from "@/services/reviews-api.service";

interface ReviewDraft {
	rating: number;
	comment: string;
}

function getInitialDraft(target: ProjectReviewEligibilityTarget): ReviewDraft {
	return {
		rating: target.review?.rating ?? 0,
		comment: target.review?.comment || "",
	};
}

export function ProjectReviewSection({
	projectId,
	eligibility,
	isLoading,
	onSaved,
}: {
	projectId: string;
	eligibility: ProjectReviewEligibility | null;
	isLoading: boolean;
	onSaved: () => Promise<void>;
}): React.ReactElement | null {
	const [drafts, setDrafts] = useState<Record<string, ReviewDraft>>({});
	const [submittingKey, setSubmittingKey] = useState<string | null>(null);

	useEffect(() => {
		if (!eligibility) return;

		const nextDrafts: Record<string, ReviewDraft> = {};
		if (eligibility.designerTarget) {
			nextDrafts[`designer:${eligibility.designerTarget.targetProfileId}`] =
				getInitialDraft(eligibility.designerTarget);
		}

		eligibility.contractorTargets.forEach((target) => {
			nextDrafts[`contractor:${target.targetProfileId}`] =
				getInitialDraft(target);
		});

		if (eligibility.realtorTarget) {
			nextDrafts[`realtor:${eligibility.realtorTarget.targetProfileId}`] =
				getInitialDraft(eligibility.realtorTarget);
		}

		setDrafts(nextDrafts);
	}, [eligibility]);

	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Reviews & Ratings</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					<Skeleton className="h-24 w-full" />
					<Skeleton className="h-24 w-full" />
				</CardContent>
			</Card>
		);
	}

	if (!eligibility) return null;

	if (
		!eligibility.designerTarget &&
		eligibility.contractorTargets.length === 0 &&
		!eligibility.realtorTarget
	) {
		return null;
	}

	// Realtor goes last because chronologically the buyer's relationship
	// with the realtor predates designer/contractor — but the most
	// recent professionals (designer, then contractors) are the ones
	// the buyer is freshest about, so we surface those first.
	const targets = [
		...(eligibility.designerTarget ? [eligibility.designerTarget] : []),
		...eligibility.contractorTargets,
		...(eligibility.realtorTarget ? [eligibility.realtorTarget] : []),
	];

	async function handleSave(
		target: ProjectReviewEligibilityTarget,
	): Promise<void> {
		const draftKey = `${target.targetType}:${target.targetProfileId}`;
		const draft = drafts[draftKey];
		if (!draft || draft.rating < 1 || draft.rating > 5) {
			toast.error("Select a rating between 1 and 5.");
			return;
		}

		setSubmittingKey(draftKey);
		const payload: CreateProjectReviewInput = {
			targetType: target.targetType,
			targetProfileId: target.targetProfileId,
			rating: draft.rating as CreateProjectReviewInput["rating"],
			comment: draft.comment.trim() || undefined,
		};

		try {
			const result = await saveProjectReview(projectId, payload);
			if (!result.success) {
				toast.error(result.error || "Failed to save review");
				return;
			}

			toast.success(target.review ? "Review updated." : "Review submitted.");
			await onSaved();
		} finally {
			setSubmittingKey(null);
		}
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle>Reviews & Ratings</CardTitle>
				<CardDescription>
					{eligibility.canReview
						? "Rate the professionals who worked on this completed project."
						: "Reviews are available after the project is completed."}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{targets.map((target) => {
					const draftKey = `${target.targetType}:${target.targetProfileId}`;
					const draft = drafts[draftKey] || getInitialDraft(target);
					const isSubmitting = submittingKey === draftKey;

					return (
						<div key={draftKey} className="rounded-lg border p-4">
							<div className="flex items-start justify-between gap-3">
								<div>
									<div className="flex items-center gap-2">
										<p className="font-medium text-foreground">
											{target.displayName}
										</p>
										<span className="rounded-full border px-2 py-0.5 text-xs capitalize text-muted-foreground">
											{target.targetType}
										</span>
										{target.review ? (
											<span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
												Submitted
											</span>
										) : null}
									</div>
									{target.review?.updatedAt ? (
										<p className="mt-1 text-xs text-muted-foreground">
											Last updated{" "}
											{new Date(target.review.updatedAt).toLocaleDateString(
												"en-IN",
											)}
										</p>
									) : null}
								</div>
							</div>

							<div className="mt-4 space-y-3">
								<div>
									<Label className="mb-2 block">Rating</Label>
									<StarRatingInput
										value={draft.rating}
										disabled={!eligibility.canReview || isSubmitting}
										onChange={(rating) =>
											setDrafts((current) => ({
												...current,
												[draftKey]: {
													...draft,
													rating,
												},
											}))
										}
									/>
									<p className="mt-2 text-xs text-muted-foreground">
										{draft.rating > 0
											? `${draft.rating} / 5 selected`
											: "Select a star rating"}
									</p>
								</div>

								<div>
									<Label htmlFor={`${draftKey}-comment`} className="mb-2 block">
										Comment
									</Label>
									<Textarea
										id={`${draftKey}-comment`}
										value={draft.comment}
										disabled={!eligibility.canReview || isSubmitting}
										onChange={(event) =>
											setDrafts((current) => ({
												...current,
												[draftKey]: {
													...draft,
													comment: event.target.value,
												},
											}))
										}
										placeholder="Share what went well and what future homeowners should know."
									/>
								</div>

								<Button
									variant="primary"
									onClick={() => void handleSave(target)}
									isLoading={isSubmitting}
									disabled={!eligibility.canReview}
								>
									{target.review ? "Update review" : "Submit review"}
								</Button>
							</div>
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}
